import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ChatsPage from './pages/ChatsPage';
import LeadsPage from './pages/LeadsPage';
import ProductsPage from './pages/ProductsPage';
import SettingsPage from './pages/SettingsPage';
import PhoneControlsPage from './pages/PhoneControlsPage';
import ConnectWhatsAppPage from './pages/ConnectWhatsAppPage';
import BookingLinkPage from './pages/BookingLinkPage';

const parseFeatures = (user) => {
  const raw = (user?.features || '').toString().trim();
  if (!raw) return null; // null => all enabled
  return new Set(
    raw
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean)
  );
};

const firstEnabledRoute = (featuresSet) => {
  // Order defines the “default landing page” — chats first, then leads.
  const ordered = [
    ['chats', '/chats'],
    ['leads', '/leads'],
    ['appointments', '/appointments'],
    ['products', '/products'],
    ['phone_controls', '/phone-controls'],
    ['settings', '/settings'],
  ];
  if (!featuresSet) return '/chats';
  for (const [k, path] of ordered) {
    if (featuresSet.has(k)) return path;
  }
  return '/settings';
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const { user } = useAuth();
  const featuresSet = parseFeatures(user);
  const defaultRoute = firstEnabledRoute(featuresSet);

  return (
    <>
      <Toaster 
        position="top-right"
        /* Sit above modal overlays (which use z-index: 9999) so toasts fired
           from inside a modal aren't hidden behind its blur. */
        containerStyle={{ zIndex: 2147483647 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            border: '1px solid #334155',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#f1f5f9',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f1f5f9',
            },
          },
        }}
      />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/" element={<Navigate to={defaultRoute} replace />} />
                  {(!featuresSet || featuresSet.has('appointments')) && (
                    <Route path="/appointments" element={<AppointmentsPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('appointments')) && (
                    <Route path="/booking-link" element={<BookingLinkPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('chats')) && (
                    <Route path="/chats" element={<ChatsPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('leads')) && (
                    <Route path="/leads" element={<LeadsPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('products')) && (
                    <Route path="/products" element={<ProductsPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('phone_controls')) && (
                    <Route path="/phone-controls" element={<PhoneControlsPage />} />
                  )}
                  {(!featuresSet || featuresSet.has('settings')) && (
                    <Route path="/settings" element={<SettingsPage />} />
                  )}
                  {/* Connect WhatsApp is always available (core onboarding) */}
                  <Route path="/connect" element={<ConnectWhatsAppPage />} />
                  {/* If user lands on a disabled/unknown route, always redirect to first enabled feature */}
                  <Route path="*" element={<Navigate to={defaultRoute} replace />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;

